import { useCallback, useContext } from "react";
import { ReactReduxContext } from "react-redux";
import {
  FlightRawEventName,
  FlightEventName,
  type FlightCoreRawEvent,
  type FlightCoreEvent,
  CORE_FLIGHTS_HTS_EVENT_TRANSFORMER_HOOKS,
  getFlightSelectedFareTrackingProperties,
  getFlightSelectedTripTrackingProperties,
  getFlightShopTrackingProperties,
  type FlightViewedListRawEvent,
  type FlightViewedListEvent,
  type FlightViewedSliceRawEvent,
  type FlightViewedSliceEvent,
  type FlightSelectedRawEvent,
  type FlightSelectedEvent,
  type FlightViewedTripSummaryRawEvent,
  type FlightViewedTripSummaryEvent,
} from "@b2bportal/core-flights";
import type {
  EventTransformerHooks,
  UseInstantTransformEvent,
} from "@b2bportal/core-tracking";
import { CallState } from "@b2bportal/core-types";
import { combineTrackingProperties } from "@hopper-b2b/utilities";
import { getRbcFlightTrackingProperties } from "./selectors";
import { Experiments } from "@/modules/experiments/src";
import { selectIsExperimentVariant } from "@/modules/experiments/src/hooks/selectExperimentVariant";
import type { RootState } from "@/store";
import { useRbcFlightEnteredTransformer } from "@/tracking/flightsTransformers";
import { getUpSellEligibilityTrackingProperties } from "@/tracking/upSell/upSellEligibility";

const commonTrackingProperties = {
  productType: "flight",
} as const;

/**
 * RBC-specific transformer for viewed_flight_list event
 * Extends core transformer with RBC tracking properties
 *
 * Uses store.getState() at execution time (not render time) so that events
 * emitted synchronously after a dispatch (e.g. tab toggle) reflect the
 * updated Redux state rather than the stale render-time snapshot.
 */
export const useRbcFlightViewedListTransformer: UseInstantTransformEvent<
  FlightViewedListRawEvent,
  FlightViewedListEvent
> = () => {
  const reduxContext = useContext(ReactReduxContext);

  return useCallback(
    (event) => {
      const state = reduxContext.store.getState() as RootState;

      // Check if price prediction experiment is enabled
      const isPricePredictionEnabled = selectIsExperimentVariant(
        state,
        Experiments.RBC_ENABLE_PRICE_PREDICTION.name,
        Experiments.RBC_ENABLE_PRICE_PREDICTION.variants.available
      );

      // Check if it's a fixed price route
      const isFixedPriceRoute =
        state.flights.flightShop.fixedPricingFlightsCallState ===
        CallState.Success;

      // Check if grid price prediction is enabled
      const isGridPricePredictionEnabled = selectIsExperimentVariant(
        state,
        Experiments.GRID_PRICE_PREDICTION_ENABLED.name,
        Experiments.GRID_PRICE_PREDICTION_ENABLED.variants.available
      );

      // Determine if recommendation should be included
      const shouldIncludeRecommendation =
        isPricePredictionEnabled &&
        (!isFixedPriceRoute || isGridPricePredictionEnabled);

      // Get recommendation from prediction
      const recommendation = shouldIncludeRecommendation
        ? state.flights.flightShop.prediction?.recommendationV2
        : undefined;

      const shopSummaryTrackingProperties =
        state.flights.flightShop.fixedPricingTrackingProperties;

      const { properties: shopSummaryProperties, encryptedProperties } =
        combineTrackingProperties(
          shopSummaryTrackingProperties ? [shopSummaryTrackingProperties] : [],
          {},
          []
        );

      return {
        name: FlightEventName.viewed_flight_list,
        properties: {
          ...commonTrackingProperties,
          ...getFlightShopTrackingProperties(state),
          ...getRbcFlightTrackingProperties(state),
          ...shopSummaryProperties,
          ...(event.funnel == null ? {} : { funnel: event.funnel }),
          recommendation,
        },
        encryptedProperties,
      };
    },
    [reduxContext.store]
  );
};

/**
 * RBC-specific transformer for viewed_slice event
 * Extends core transformer with RBC tracking properties
 */
export const useRbcFlightViewedSliceTransformer: UseInstantTransformEvent<
  FlightViewedSliceRawEvent,
  FlightViewedSliceEvent
> = () => {
  const reduxContext = useContext(ReactReduxContext);

  return useCallback(() => {
    const state = reduxContext.store.getState() as RootState;

    const rbcProperties = getRbcFlightTrackingProperties(state);

    const event: FlightViewedSliceEvent = {
      name: FlightEventName.viewed_slice,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(state),
        ...getFlightSelectedTripTrackingProperties(state),
        ...rbcProperties,
      },
    };

    return event;
  }, [reduxContext.store]);
};

/**
 * RBC-specific transformer for selected_flight event
 * Extends core transformer with RBC tracking properties
 *
 * Uses store.getState() at execution time (not render time) so that events
 * emitted after trip details are fetched reflect the updated Redux state
 * rather than stale render-time snapshots.
 */
export const useRbcFlightSelectedFlightTransformer: UseInstantTransformEvent<
  FlightSelectedRawEvent,
  FlightSelectedEvent
> = () => {
  const reduxContext = useContext(ReactReduxContext);

  return useCallback(() => {
    const state = reduxContext.store.getState() as RootState;

    // Get the selected trip details which contains tracking properties
    const selectedTrip = state.flights?.flightShop?.selectedTrip;
    const tripId = selectedTrip?.tripId;
    const tripDetails = tripId
      ? state.flights?.flightShop?.tripDetailsById?.[tripId]
      : undefined;

    // Extract tracking properties from trip details (same pattern as shopSummary)
    const tripTrackingProperties = tripDetails?.trackingProperties;

    // Combine tracking properties to separate regular and encrypted properties
    const { properties: tripDetailsProperties, encryptedProperties } =
      combineTrackingProperties(
        tripTrackingProperties ? [tripTrackingProperties] : [],
        {},
        []
      );

    return {
      name: FlightEventName.selected_flight,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(state),
        ...getFlightSelectedTripTrackingProperties(state),
        ...getFlightSelectedFareTrackingProperties(state),
        ...getRbcFlightTrackingProperties(state),
        ...tripDetailsProperties,
      },
      encryptedProperties,
    };
  }, [reduxContext.store]);
};

/**
 * RBC-specific transformer for viewed_trip_summary event
 * Extends core transformer with RBC tracking properties
 */
export const useRbcFlightViewedTripSummaryTransformer: UseInstantTransformEvent<
  FlightViewedTripSummaryRawEvent,
  FlightViewedTripSummaryEvent
> = () => {
  const reduxContext = useContext(ReactReduxContext);

  return useCallback(() => {
    const state = reduxContext.store.getState() as RootState;

    return {
      name: FlightEventName.viewed_trip_summary,
      properties: {
        ...commonTrackingProperties,
        ...getFlightShopTrackingProperties(state),
        ...getFlightSelectedTripTrackingProperties(state),
        ...getRbcFlightTrackingProperties(state),
        // Set in every experiment leg, control included.
        ...getUpSellEligibilityTrackingProperties(state),
      },
    };
  }, [reduxContext.store]);
};

/**
 * RBC-specific event transformer hooks
 * Overrides core transformers with RBC-enhanced versions
 */
export const RBC_FLIGHT_HTS_EVENT_TRANSFORMER_HOOKS: EventTransformerHooks<
  FlightCoreRawEvent,
  FlightCoreEvent
> = {
  [FlightRawEventName.ViewedFlightList]: [useRbcFlightViewedListTransformer],
  [FlightRawEventName.ViewedSlice]: [useRbcFlightViewedSliceTransformer],
  [FlightRawEventName.SelectedFlight]: [useRbcFlightSelectedFlightTransformer],
  [FlightRawEventName.ViewedTripSummary]: [
    useRbcFlightViewedTripSummaryTransformer,
  ],
  [FlightRawEventName.EnteredFlight]: [useRbcFlightEnteredTransformer],
};

/**
 * All RBC flight transformers - core transformers with RBC overrides
 * RBC transformers REPLACE core transformers for the same event (not merge)
 */
export const ALL_RBC_FLIGHT_TRANSFORMERS: EventTransformerHooks<
  FlightCoreRawEvent,
  FlightCoreEvent
> = {
  // Start with all core transformers
  ...CORE_FLIGHTS_HTS_EVENT_TRANSFORMER_HOOKS,
  // Override with RBC-specific transformers (replaces, not merges)
  ...RBC_FLIGHT_HTS_EVENT_TRANSFORMER_HOOKS,
};
